import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../../lib/api';
import { formatCurrency } from '@outlaw-oasis/utils';

interface Product {
  id: string;
  name: string;
  slug: string;
  price: number;
  status: string;
  stockType: string;
  stockQuantity: number;
  salesCount: number;
  thumbnailUrl: string | null;
  createdAt: string;
}

interface KeyStats {
  total: number;
  available: number;
  sold: number;
}

export default function SellerDashboard() {
  const queryClient = useQueryClient();
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [newKeys, setNewKeys] = useState('');
  const [showAddKeys, setShowAddKeys] = useState(false);

  // Fetch dashboard data
  const { data: dashboardData, isLoading: dashboardLoading } = useQuery({
    queryKey: ['seller-dashboard'],
    queryFn: () => api.get('/sellers/dashboard'),
  });

  // Fetch seller's products
  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ['seller-products'],
    queryFn: async () => {
      const dashboard = await api.get('/sellers/dashboard') as any;
      const sellerId = dashboard?.data?.seller?.id;
      if (!sellerId) return { data: { products: [] } };
      return api.get(`/products?sellerId=${sellerId}&limit=100`);
    },
  });

  // Fetch keys for selected product
  const { data: keysData, isLoading: keysLoading } = useQuery({
    queryKey: ['product-keys', selectedProduct],
    queryFn: () => api.get(`/products/${selectedProduct}/keys`),
    enabled: !!selectedProduct,
  });

  // Add keys mutation
  const addKeysMutation = useMutation({
    mutationFn: async ({ productId, keys }: { productId: string; keys: string }) => {
      return api.post(`/products/${productId}/keys`, { keys });
    },
    onSuccess: (data: any) => {
      toast.success(data.message || 'Keys added successfully');
      setNewKeys('');
      setShowAddKeys(false);
      queryClient.invalidateQueries({ queryKey: ['product-keys', selectedProduct] });
      queryClient.invalidateQueries({ queryKey: ['seller-products'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add keys');
    },
  });

  const dashboard = (dashboardData as any)?.data;
  const products = (productsData as any)?.data?.products || [];
  const keys = (keysData as any)?.data?.keys || [];
  const keyStats: KeyStats = (keysData as any)?.data?.stats || { total: 0, available: 0, sold: 0 };

  const handleAddKeys = () => {
    if (!selectedProduct || !newKeys.trim()) return;
    addKeysMutation.mutate({ productId: selectedProduct, keys: newKeys });
  };

  if (dashboardLoading) {
    return (
      <div>
        <h1 className="font-western text-3xl text-cream mb-8">Seller Dashboard</h1>
        <div className="animate-pulse space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="card p-6">
                <div className="h-4 bg-leather rounded w-1/2 mb-2" />
                <div className="h-8 bg-leather rounded w-3/4" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!dashboard?.seller) {
    return (
      <div>
        <h1 className="font-western text-3xl text-cream mb-8">Seller Dashboard</h1>
        <div className="card p-8 text-center">
          <p className="text-text-secondary mb-4">You are not a seller yet.</p>
          <p className="text-text-muted text-sm">Apply to become a seller or use an invite code.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-western text-3xl text-cream mb-8">Seller Dashboard</h1>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="card p-6">
          <h3 className="text-text-muted text-sm uppercase tracking-wider mb-2">Total Revenue</h3>
          <p className="text-3xl font-bold text-gold">{formatCurrency(dashboard.stats.totalRevenue)}</p>
        </div>
        <div className="card p-6">
          <h3 className="text-text-muted text-sm uppercase tracking-wider mb-2">Available Balance</h3>
          <p className="text-3xl font-bold text-cream">{formatCurrency(dashboard.stats.availableBalance)}</p>
        </div>
        <div className="card p-6">
          <h3 className="text-text-muted text-sm uppercase tracking-wider mb-2">Total Orders</h3>
          <p className="text-3xl font-bold text-cream">{dashboard.stats.totalOrders}</p>
        </div>
        <div className="card p-6">
          <h3 className="text-text-muted text-sm uppercase tracking-wider mb-2">Active Products</h3>
          <p className="text-3xl font-bold text-cream">{dashboard.stats.activeProducts}</p>
        </div>
      </div>

      {/* Products & Keys Management */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Products List */}
        <div className="card p-6">
          <h2 className="font-semibold text-cream mb-4">Your Products</h2>

          {productsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-leather rounded animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <p className="text-text-secondary">No products yet.</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {products.map((product: Product) => (
                <button
                  key={product.id}
                  onClick={() => setSelectedProduct(product.id)}
                  className={`w-full text-left p-4 rounded-lg border transition-colors ${
                    selectedProduct === product.id
                      ? 'border-gold bg-gold/10'
                      : 'border-gold/10 hover:border-gold/30'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-leather rounded overflow-hidden flex-shrink-0">
                      {product.thumbnailUrl ? (
                        <img src={product.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-gold/30 text-xs">OO</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-cream font-medium truncate">{product.name}</h3>
                      <div className="flex items-center gap-3 text-sm">
                        <span className="text-gold">{formatCurrency(product.price)}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          product.status === 'active' ? 'bg-success/20 text-success' :
                          product.status === 'draft' ? 'bg-text-muted/20 text-text-muted' :
                          product.status === 'pending_review' ? 'bg-gold/20 text-gold' :
                          'bg-error/20 text-error'
                        }`}>
                          {product.status}
                        </span>
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      <div className="text-text-muted">Stock</div>
                      <div className="text-cream">
                        {product.stockType === 'unlimited' ? 'Unlimited' : product.stockQuantity}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Keys Management */}
        <div className="card p-6">
          <h2 className="font-semibold text-cream mb-4">Product Keys</h2>

          {!selectedProduct ? (
            <p className="text-text-secondary">Select a product to manage its keys.</p>
          ) : keysLoading ? (
            <div className="animate-pulse space-y-3">
              <div className="h-20 bg-leather rounded" />
              <div className="h-40 bg-leather rounded" />
            </div>
          ) : (
            <>
              {/* Key Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-leather rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-cream">{keyStats.total}</div>
                  <div className="text-text-muted text-xs">Total</div>
                </div>
                <div className="bg-leather rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-success">{keyStats.available}</div>
                  <div className="text-text-muted text-xs">Available</div>
                </div>
                <div className="bg-leather rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-gold">{keyStats.sold}</div>
                  <div className="text-text-muted text-xs">Sold</div>
                </div>
              </div>

              {/* Add Keys Button */}
              {!showAddKeys && (
                <button
                  onClick={() => setShowAddKeys(true)}
                  className="btn-primary w-full mb-4"
                >
                  + Add Keys
                </button>
              )}

              {/* Add Keys Form */}
              {showAddKeys && (
                <div className="mb-6">
                  <label className="block text-text-secondary text-sm mb-2">
                    Enter keys (one per line)
                  </label>
                  <textarea
                    value={newKeys}
                    onChange={(e) => setNewKeys(e.target.value)}
                    placeholder="KEY-XXXX-XXXX-XXXX&#10;KEY-YYYY-YYYY-YYYY&#10;KEY-ZZZZ-ZZZZ-ZZZZ"
                    className="w-full h-32 px-4 py-3 bg-background border border-gold/20 rounded-lg text-cream placeholder-text-muted font-mono text-sm focus:outline-none focus:border-gold/50 resize-none"
                  />
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={handleAddKeys}
                      disabled={!newKeys.trim() || addKeysMutation.isPending}
                      className="btn-primary flex-1"
                    >
                      {addKeysMutation.isPending ? 'Adding...' : 'Add Keys'}
                    </button>
                    <button
                      onClick={() => {
                        setShowAddKeys(false);
                        setNewKeys('');
                      }}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Keys List */}
              {keys.length > 0 && (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  <div className="text-text-muted text-sm mb-2">Recent Keys</div>
                  {keys.slice(0, 20).map((key: any) => (
                    <div
                      key={key.id}
                      className={`flex items-center justify-between px-3 py-2 rounded ${
                        key.status === 'available' ? 'bg-success/10' : 'bg-leather'
                      }`}
                    >
                      <code className="text-sm font-mono text-cream truncate flex-1">
                        {key.status === 'sold' ? '****' + key.keyValue.slice(-8) : key.keyValue}
                      </code>
                      <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                        key.status === 'available' ? 'bg-success/20 text-success' :
                        key.status === 'sold' ? 'bg-gold/20 text-gold' :
                        'bg-text-muted/20 text-text-muted'
                      }`}>
                        {key.status}
                      </span>
                    </div>
                  ))}
                  {keys.length > 20 && (
                    <p className="text-text-muted text-sm text-center">
                      ...and {keys.length - 20} more keys
                    </p>
                  )}
                </div>
              )}

              {keys.length === 0 && !showAddKeys && (
                <p className="text-text-secondary text-center py-4">
                  No keys added yet. Add keys to enable stock-based delivery.
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Recent Orders */}
      <div className="card p-6 mt-6">
        <h2 className="font-semibold text-cream mb-4">Recent Orders</h2>
        {dashboard.recentOrders.length === 0 ? (
          <p className="text-text-secondary">No orders yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-text-muted text-sm border-b border-gold/10">
                  <th className="pb-3">Order</th>
                  <th className="pb-3">Date</th>
                  <th className="pb-3">Status</th>
                  <th className="pb-3 text-right">Your Earnings</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.recentOrders.map((order: any) => (
                  <tr key={order.id} className="border-b border-gold/5">
                    <td className="py-3 text-gold font-mono">#{order.orderNumber}</td>
                    <td className="py-3 text-text-secondary">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded text-xs ${
                        order.status === 'completed' ? 'bg-success/20 text-success' :
                        'bg-gold/20 text-gold'
                      }`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="py-3 text-right text-cream font-semibold">
                      {formatCurrency(order.sellerTotal)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
